import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Instagram, Mail, Check } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FALLBACK_ABOUT = {
  heading: "I kept buying products that weren\u2019t built for my skin.",
  story_paragraphs: [
    "Most skincare is formulated for European skin, tested in European weather, marketed to women.",
    "TRUFORM MEN started from a simple observation \u2014 Indian men live differently, and their products should reflect that. Hard water at 300ppm. UV index 10. 80% humidity. A 3-minute morning routine.",
    "We built around all of it."
  ],
  commitments: [
    "Every ingredient explained on the product and on this site.",
    "No synthetic fragrance \u2014 IFRA-certified only.",
    "No fairness or whitening claims \u2014 ever.",
    "SPF claims tested at NABL-accredited labs.",
    "Formula IP owned by TRUFORM MEN.",
    "Hard water tested at 300ppm on every formula."
  ],
  instagram_handle: "@truformmen",
  instagram_url: "https://instagram.com/truformmen",
  email: "hello@truformmen.com"
};

export default function AboutPage() {
  const navigate = useNavigate();
  const [ab, setAb] = useState(FALLBACK_ABOUT);

  useEffect(() => {
    axios.get(`${API}/site-content`).then(r => {
      if (r.data?.about) setAb(prev => ({ ...prev, ...r.data.about }));
    }).catch(err => console.error('Failed to load site content:', err));
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="bg-[#F0F9FF]">
      {/* Header */}
      <section className="bg-[#0C4A8C] py-12 md:py-16">
        <div className="max-w-3xl mx-auto px-6 md:px-10">
          <button onClick={() => navigate(-1)} data-testid="back-button" className="inline-flex items-center gap-2 text-white/30 hover:text-white text-[12px] mb-6 transition-colors">
            <ArrowLeft size={13} /> Back
          </button>
          <p className="eyebrow mb-3 text-[#BAE6FD]">About TRUFORM MEN</p>
          <h1 data-testid="about-heading" className="text-3xl md:text-4xl font-serif font-medium text-white tracking-tight leading-tight">
            {ab.heading}
          </h1>
        </div>
      </section>

      {/* Founder Story */}
      <section data-testid="founder-story" className="bg-white max-w-3xl mx-auto mt-5 rounded-[4px] py-12 md:py-16 px-6 md:px-10" style={{ boxShadow: '0 1px 3px rgba(12,74,140,0.06)' }}>
        <div className="space-y-5">
          {ab.story_paragraphs.map((p) => (
            <p key={p} className="text-[14px] text-[#3D4F5C] font-light leading-relaxed">{p}</p>
          ))}
        </div>
      </section>

      {/* Commitments */}
      <section data-testid="commitments-section" className="bg-[#0C4A8C] py-12 md:py-16 mt-2">
        <div className="max-w-3xl mx-auto px-6 md:px-10">
          <p className="eyebrow mb-3 text-[#BAE6FD]">Our commitments</p>
          <h2 className="text-2xl md:text-3xl font-serif font-medium text-white tracking-tight mb-10">What we stand for.</h2>
          <div className="space-y-4">
            {ab.commitments.map((c) => (
              <div key={c} className="flex items-start gap-3">
                <div className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5" style={{ border: '1px solid rgba(186,230,253,0.3)' }}>
                  <Check size={10} className="text-[#BAE6FD]" />
                </div>
                <p className="text-[14px] font-light leading-relaxed text-white/50">{c}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact */}
      <section data-testid="contact-section" className="bg-white max-w-3xl mx-auto mt-5 mb-5 rounded-[4px] py-12 md:py-16 px-6 md:px-10" style={{ boxShadow: '0 1px 3px rgba(12,74,140,0.06)' }}>
        <p className="eyebrow text-[#0C7BC0] mb-3">Find us</p>
        <div className="flex flex-col sm:flex-row gap-6 mb-10">
          <a href={ab.instagram_url} target="_blank" rel="noopener noreferrer" data-testid="about-instagram" className="flex items-center gap-2 text-[#6B7D8A] hover:text-[#0C4A8C] transition-colors text-[13px]">
            <Instagram size={16} strokeWidth={1.5} /> {ab.instagram_handle}
          </a>
          <a href={`mailto:${ab.email}`} data-testid="about-email" className="flex items-center gap-2 text-[#6B7D8A] hover:text-[#0C4A8C] transition-colors text-[13px]">
            <Mail size={16} strokeWidth={1.5} /> {ab.email}
          </a>
        </div>
        <ContactForm />
      </section>
    </div>
  );
}

function ContactForm() {
  const [form, setForm] = useState({ name: '', email: '', message: '' });
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, form);
      setSubmitted(true);
      setForm({ name: '', email: '', message: '' });
    } catch (err) {
      console.error('Contact form submission failed:', err);
    }
    setSubmitting(false);
  };

  return (
    <div className="pt-10" style={{ borderTop: '1px solid rgba(12,74,140,0.06)' }}>
      <h3 className="font-serif text-xl text-[#0C4A8C] font-medium mb-6">Send us a message</h3>
      {submitted ? (
        <div data-testid="contact-success" className="bg-[#F0F9FF] p-8 rounded-[2px] text-center">
          <Check size={20} className="text-[#0C7BC0] mx-auto mb-3" />
          <p className="text-[14px] text-[#0C4A8C] font-serif">Message sent. We'll get back to you.</p>
        </div>
      ) : (
        <form onSubmit={handleSubmit} data-testid="contact-form" className="space-y-4">
          <div>
            <label className="eyebrow text-[#6B7D8A] mb-1.5 block">Name</label>
            <input data-testid="contact-name-input" type="text" value={form.name} onChange={e => setForm({...form, name: e.target.value})}
              className="w-full bg-white border text-[#0C4A8C] px-4 py-2.5 rounded-[2px] focus:outline-none focus:border-[#0C4A8C] placeholder-[#6B7D8A] text-sm font-light" style={{ borderColor: 'rgba(12,74,140,0.1)' }} placeholder="Your name" required />
          </div>
          <div>
            <label className="eyebrow text-[#6B7D8A] mb-1.5 block">Email</label>
            <input data-testid="contact-email-input" type="email" value={form.email} onChange={e => setForm({...form, email: e.target.value})}
              className="w-full bg-white border text-[#0C4A8C] px-4 py-2.5 rounded-[2px] focus:outline-none focus:border-[#0C4A8C] placeholder-[#6B7D8A] text-sm font-light" style={{ borderColor: 'rgba(12,74,140,0.1)' }} placeholder="your@email.com" required />
          </div>
          <div>
            <label className="eyebrow text-[#6B7D8A] mb-1.5 block">Message</label>
            <textarea data-testid="contact-message-input" value={form.message} onChange={e => setForm({...form, message: e.target.value})} rows={4}
              className="w-full bg-white border text-[#0C4A8C] px-4 py-2.5 rounded-[2px] focus:outline-none focus:border-[#0C4A8C] placeholder-[#6B7D8A] text-sm font-light resize-none" style={{ borderColor: 'rgba(12,74,140,0.1)' }} placeholder="Your message..." required />
          </div>
          <button type="submit" data-testid="contact-submit-btn" disabled={submitting}
            className="bg-[#0C4A8C] text-[#F0F9FF] px-7 py-3 rounded-[2px] text-[11px] font-sans font-medium uppercase tracking-[3px] hover:bg-[#093B70] transition-colors disabled:opacity-50">
            {submitting ? 'Sending...' : 'Send Message'}
          </button>
        </form>
      )}
    </div>
  );
}
